import { PRODUCTS } from "./products";
import { seafoodItems } from "./seafood";
import { SITE_PAGES } from "./site";
import { CITIES } from "./locations";
import { FAQS } from "./faq";

export type SearchCategory = "Menu" | "Product" | "Page" | "Location" | "FAQ";

/**
 * What the site search (the navbar's search box, Ctrl+K) shows: catches on the MENU, the PRODUCTS & services, and the
 * site's PAGES — in that order of preference. Cities and FAQs stay in SEARCH_INDEX because the chatbot answers from
 * them, but they are not "things to go to" and would only look like random hits in the search box.
 */
export const SITE_SEARCH_CATEGORIES: SearchCategory[] = ["Menu", "Product", "Page"];

export interface SearchEntry {
  id: string;
  title: string;
  subtitle?: string;
  path: string;
  keywords: string[];
  category: SearchCategory;
}

// Built once, client-side, from the same data modules the pages themselves
// use (src/data/site.ts, Locations.tsx, Contact.tsx) — so there's exactly
// one place to update a page/city/FAQ, not a second index to keep in sync.
// No press-mention entries: ZP-96's press strip ships empty (no fabricated
// coverage), so there's nothing real to index yet either.
export const SEARCH_INDEX: SearchEntry[] = [
  // The catches on the menu. Each opens that catch's own page, /menu/<id> (pages/ProductPage.tsx).
  ...seafoodItems.map((item) => ({
    id: `menu-${item.id}`,
    title: item.name,
    subtitle: `${item.category} · ${item.price} ${item.unit} · The Catch`,
    path: `/menu/${item.id}`,
    keywords: [
      ...item.name.toLowerCase().replace(/[()]/g, " ").split(/\s+/).filter(Boolean),
      item.category.toLowerCase(),
      ...item.tags.map((tag) => tag.toLowerCase()),
    ],
    category: "Menu" as const,
  })),
  // The ZenFresh products & services (Customer App, Driver App, …) described on /products.
  ...PRODUCTS.map((product) => ({
    id: `product-${product.id}`,
    title: product.name,
    subtitle: product.summary,
    path: "/products",
    keywords: [...product.name.toLowerCase().split(/\s+/), "app", "product", "service"],
    category: "Product" as const,
  })),
  ...SITE_PAGES.map((p) => ({
    id: `page-${p.path}`,
    title: p.title,
    subtitle: p.description,
    path: p.path,
    keywords: p.keywords,
    category: "Page" as const,
  })),
  ...CITIES.map((c) => ({
    id: `city-${c.name}`,
    title: c.name,
    subtitle: `${c.state} · Delivery location`,
    path: "/locations",
    keywords: [c.name.toLowerCase(), c.state.toLowerCase()],
    category: "Location" as const,
  })),
  ...FAQS.map((f, i) => ({
    id: `faq-${i}`,
    title: f.q,
    subtitle: f.a,
    // The FAQ accordion moved from /contact to /partnerships#faq for ZP-85
    // (grouped by Supplier/Franchise/B2B/General) — every entry points here
    // regardless of category since it's one shared accordion section, not
    // per-category anchors.
    path: "/partnerships#faq",
    keywords: f.q.toLowerCase().split(/\s+/),
    category: "FAQ" as const,
  })),
];

/** Simple relevance-ranked substring/prefix scorer — plenty fast for an index this size. Menu, products and pages only. */
export function searchSite(query: string, limit = 8): SearchEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const scored = SEARCH_INDEX.filter((entry) => SITE_SEARCH_CATEGORIES.includes(entry.category)).map((entry) => {
    const title = entry.title.toLowerCase();
    let score = 0;
    if (title === q) score = 100;
    else if (title.startsWith(q)) score = 80;
    else if (title.includes(q)) score = 60;
    else if (entry.keywords.some((k) => k.startsWith(q))) score = 50;
    else if (entry.keywords.some((k) => k.includes(q))) score = 30;
    else if (entry.subtitle?.toLowerCase().includes(q)) score = 15;
    return { entry, score };
  })
    .filter((r) => r.score > 0)
    // Best match first; on a tie a menu item beats a product beats a page.
    .sort((a, b) => b.score - a.score || SITE_SEARCH_CATEGORIES.indexOf(a.entry.category) - SITE_SEARCH_CATEGORIES.indexOf(b.entry.category));

  return scored.slice(0, limit).map((r) => r.entry);
}
